/**
 * AuthService - wraps the Firebase Auth SDK (CLOUD_SYNC_SPEC.md §7, §10)
 *
 * Sign-in/registration happen entirely client-side in the SDK (providers,
 * password reset, session refresh); the Worker only ever sees short-lived
 * ID tokens. The firebase chunk is loaded lazily so the core app doesn't
 * pay for it until auth is touched (init runs at startup to restore an
 * existing session, but off the critical path).
 */
import { getFirebaseApp } from './firebaseApp.js';
import { track } from './Analytics.js';

export class AuthService {
    constructor() {
        this.currentUser = null;     // Firebase user | null
        this.ready = false;          // first onAuthStateChanged fired
        this._auth = null;
        this._fb = null;             // firebase/auth module namespace
        this._listeners = new Set();
        this._initPromise = null;
    }

    /** Lazy-init the Firebase SDK; resolves after the first auth state. */
    init() {
        if (this._initPromise) return this._initPromise;
        this._initPromise = (async () => {
            const [app, fb] = await Promise.all([
                getFirebaseApp(),
                import('firebase/auth')
            ]);
            this._fb = fb;
            this._auth = fb.getAuth(app);
            await new Promise((resolve) => {
                fb.onAuthStateChanged(this._auth, (user) => {
                    this.currentUser = user;
                    this.ready = true;
                    this._notify();
                    resolve();
                });
            });
            return this;
        })();
        return this._initPromise;
    }

    /** Subscribe to auth changes; fires immediately if state is known. */
    onAuthChange(fn) {
        this._listeners.add(fn);
        if (this.ready) fn(this.currentUser);
        return () => this._listeners.delete(fn);
    }

    _notify() {
        for (const fn of this._listeners) {
            try { fn(this.currentUser); } catch (e) { console.error(e); }
        }
    }

    get isSignedIn() {
        return !!this.currentUser;
    }

    /** Fresh ID token for API calls (SDK handles refresh) or null. */
    async getIdToken() {
        if (!this.currentUser) return null;
        return this.currentUser.getIdToken();
    }

    async signInGoogle() {
        await this.init();
        const provider = new this._fb.GoogleAuthProvider();
        await this._fb.signInWithPopup(this._auth, provider);
        track('login', { method: 'google' });
        return this.currentUser;
    }

    async signInEmail(email, password) {
        await this.init();
        await this._fb.signInWithEmailAndPassword(this._auth, email, password);
        track('login', { method: 'password' });
        return this.currentUser;
    }

    async registerEmail(email, password) {
        await this.init();
        await this._fb.createUserWithEmailAndPassword(this._auth, email, password);
        track('sign_up', { method: 'password' });
        return this.currentUser;
    }

    async resetPassword(email) {
        await this.init();
        await this._fb.sendPasswordResetEmail(this._auth, email);
    }

    async signOut() {
        if (!this._auth) return;
        await this._fb.signOut(this._auth);
    }

    /** Human-readable message for Firebase auth errors */
    static friendlyError(e) {
        const code = e?.code || '';
        const map = {
            'auth/invalid-credential': 'Wrong email or password',
            'auth/invalid-email': 'That email address looks invalid',
            'auth/user-not-found': 'No account with that email',
            'auth/wrong-password': 'Wrong email or password',
            'auth/email-already-in-use': 'An account with that email already exists',
            'auth/weak-password': 'Password must be at least 6 characters',
            'auth/popup-closed-by-user': 'Sign-in window was closed',
            'auth/popup-blocked': 'Your browser blocked the sign-in popup',
            'auth/network-request-failed': 'Network error - are you offline?'
        };
        return map[code] || e?.message || 'Sign-in failed';
    }
}
